import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { IoMdLock } from "react-icons/io";
import { CiCircleQuestion } from "react-icons/ci";
import { useNavigate } from "react-router-dom";
import * as flags from "country-flag-icons/react/3x2";
import toast from "react-hot-toast";
import {
  parsePhoneNumberFromString,
  getCountryCallingCode,
  getCountries,
} from "libphonenumber-js";
import { REST_API_URL } from "../../api/config";

const BASE_URL = REST_API_URL;

const getUserTimeZone = () => {
  if (typeof window === "undefined") return "UTC";
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
};

const buildHeaders = (token, extra = {}) => ({
  "Content-Type": "application/json",
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
  "User-Timezone": getUserTimeZone(),
  ...extra,
});

export default function BiographySimple() {
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState("US");
  const [countryCode, setCountryCode] = useState("+1");
  const [openCountries, setOpenCountries] = useState(false);
  const [userTimeZone, setUserTimeZone] = useState("UTC");

  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const timeZone = getUserTimeZone();
    setUserTimeZone(timeZone);
    localStorage.setItem("user_timezone", timeZone);
    localStorage.setItem("user_status", "worker");
    localStorage.setItem("onboarding_code", "234");
    localStorage.setItem("worker_biography_start", new Date().toISOString());
    localStorage.setItem("worker_biography_timezone", timeZone);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenCountries(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setSelectedCountry("US");
    setCountryCode("+1");
  }, []);

  const handlePhoneChange = (value) => {
    const cleaned = value.replace(/[^\d+\-\s]/g, "");
    setPhone(cleaned);

    const parsed = parsePhoneNumberFromString(cleaned, selectedCountry);

    if (!parsed || !parsed.isValid()) {
      setErrors((prev) => ({
        ...prev,
        phone: "Please enter a valid phone number.",
      }));
      return;
    }

    setErrors((prev) => {
      const next = { ...prev };
      delete next.phone;
      return next;
    });
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!fullName.trim() || fullName.trim().length < 2) {
      nextErrors.fullName = "Full name is required (min 2 characters).";
    }

    const parsed = parsePhoneNumberFromString(phone, selectedCountry);
    if (!parsed || !parsed.isValid()) {
      nextErrors.phone = "Please enter a valid phone number.";
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      toast.error("Please fix the highlighted errors.");
      return false;
    }

    return true;
  };

  const getValidToken = async () => {
    let token =
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("accessToken") ||
      "";

    token = String(token || "").trim();
    if (!token) return null;

    const verifyRes = await fetch(`${BASE_URL}/auth/token/verify/`, {
      method: "POST",
      headers: buildHeaders(null),
      body: JSON.stringify({ token }),
    });

    if (verifyRes.ok) return token;

    const refresh =
      localStorage.getItem("refreshToken") ||
      sessionStorage.getItem("refreshToken") ||
      "";

    if (!String(refresh).trim()) return null;

    const refreshRes = await fetch(`${BASE_URL}/auth/token/refresh/`, {
      method: "POST",
      headers: buildHeaders(null),
      body: JSON.stringify({ refresh: String(refresh).trim() }),
    });

    const data = await refreshRes.json().catch(() => ({}));

    if (refreshRes.ok && data.access) {
      if (localStorage.getItem("accessToken") || localStorage.getItem("refreshToken")) {
        localStorage.setItem("accessToken", data.access);
      } else {
        sessionStorage.setItem("accessToken", data.access);
      }

      return data.access;
    }

    return null;
  };

  const handleNext = async () => {
    if (!validateForm()) return;

    setLoading(true);

    try {
      const token = await getValidToken();

      if (!token) {
        toast.error("Session expired. Please log in again.");
        navigate("/sign-in", { replace: true });
        return;
      }

      const parsed = parsePhoneNumberFromString(phone, selectedCountry);
      const normalizedPhone = parsed?.number || phone;
      const timeZone = getUserTimeZone();

      const body = {
        full_name: fullName.trim(),
        phone_number: normalizedPhone,
        timeZone: timeZone,
        country: selectedCountry,
      };

      const res = await fetch(`${BASE_URL}/auth/userinfo/`, {
        method: "POST",
        headers: buildHeaders(token),
        body: JSON.stringify(body),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.detail || "Failed to save data.");
      }

      localStorage.setItem("worker_biography_saved", new Date().toISOString());
      localStorage.setItem("worker_name", fullName.trim());
      localStorage.setItem("worker_phone", normalizedPhone);
      localStorage.setItem("worker_country", selectedCountry);
      localStorage.setItem("user_status", "worker");
      localStorage.setItem("user_timezone", timeZone);
      localStorage.setItem("onboarding_code", "236");

      sessionStorage.setItem("onboarding_force_route", "/working-days");
      sessionStorage.setItem("onboarding_force_status", "worker");

      toast.success("Information saved successfully!");

      navigate("/working-days", {
        replace: true,
        state: {
          timeZone,
          fromWorkerBiography: true,
        },
      });
    } catch (err) {
      console.error("Worker biography save error:", err);
      toast.error(err.message || "Failed to save information.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (e.key === "Enter" && fullName.trim() && phone.trim() && !loading) {
        e.preventDefault();
        handleNext();
      }
    };

    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown);
  }, [fullName, phone, loading]);

  return (
    <motion.div
      className="biography"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <main className="biography-main">
        <motion.div
          className="biography-card biography-card-worker"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="biography-header">
            <div className="biography-text">
              <h2>Biography</h2>
              <p>Tell us about yourself</p>
            </div>
          </div>

          <div
            className="biography-content biography-content-worker"
            ref={dropdownRef}
          >
            <div className="biography-form-group">
              <label>
                Full name <span className="required">*</span>
              </label>
              <input
                type="text"
                placeholder="Type your full name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className={errors.fullName ? "invalid" : ""}
                autoFocus
                disabled={loading}
              />
              {errors.fullName && (
                <span className="input-error-text">{errors.fullName}</span>
              )}
            </div>

            <div className="biography-form-group">
              <label>
                Phone Number <span className="required">*</span>
              </label>

              <div className="phone-field">
                <div
                  className="phone-prefix"
                  onClick={() => setOpenCountries((prev) => !prev)}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setOpenCountries((prev) => !prev);
                    }
                  }}
                >
                  {React.createElement(flags[selectedCountry] || flags.US, {
                    title: selectedCountry,
                    style: { width: 24, height: 18, marginRight: 6 },
                  })}
                  <span className="country-code">{countryCode}</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M7 10l5 5 5-5"
                      stroke="#9A99AB"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>

                <input
                  type="tel"
                  inputMode="tel"
                  pattern="[0-9+\-\s]*"
                  value={phone}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  className={`phone-input ${errors.phone ? "invalid" : ""}`}
                  disabled={loading}
                  placeholder="Phone number"
                />

                {openCountries && (
                  <div className="country-dropdown">
                    {getCountries().map((c) => (
                      <div
                        key={c}
                        className="country-item"
                        onClick={() => {
                          setSelectedCountry(c);
                          setCountryCode("+" + getCountryCallingCode(c));
                          setOpenCountries(false);
                        }}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            setSelectedCountry(c);
                            setCountryCode("+" + getCountryCallingCode(c));
                            setOpenCountries(false);
                          }
                        }}
                      >
                        {React.createElement(flags[c] || flags.US, {
                          title: c,
                          style: { width: 20, height: 14, marginRight: 8 },
                        })}
                        <span className="country-name">{c}</span>
                        <span className="country-dial">
                          +{getCountryCallingCode(c)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {errors.phone && (
                <span className="input-error-text">{errors.phone}</span>
              )}
            </div>
          </div>

          <div className="biography-btn-footer">
            <motion.button
              className="biography-next-btn biography-next-btn-worker"
              onClick={handleNext}
              disabled={loading || !fullName.trim() || !phone.trim()}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {loading ? "Saving..." : "Next"}
            </motion.button>

            <span className="biography-step">Step 1/2</span>
          </div>
        </motion.div>
      </main>
    </motion.div>
  );
}